/**
 * `GET /api/tools` and `GET /api/usage` (slice 2a part 2 §5). Both read every
 * time their page opens and when the window regains focus (`staleTime: 0`:
 * a lookup since the last read must show, Devin on #212); neither updates
 * live.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "../../app/api";

export type ToolId =
  | "search_web"
  | "read_page"
  | "browser"
  | "files"
  | "tasks"
  | "docs"
  | "crew_files";
export type ToolState = "on" | "capped" | "unavailable";

export type Tool = {
  id: ToolId;
  group: "web" | "computer" | "crew";
  state: ToolState;
  note: string | null;
  enabled: boolean;
  can_turn_off: boolean;
};

export type ToolsResponse = { tools: Tool[]; web: { limit: number } };

export type Usage = {
  plan: { name: string; price_usd: number; status: string };
  web: { today: number; limit: number; resets_at: string };
  month: { since: string; search_web: number; read_page: number };
  /** Slice 5b (spec §10): the files the workspace keeps, in bytes; missing on an older Worker. */
  storage?: { used: number; limit: number };
};

export const toolsKey = ["settings", "tools"] as const;
export const usageKey = ["settings", "usage"] as const;
/** Settings → Integrations and Browse apps (slice 3b spec §5.1, §5.6): both under this key. */
export const integrationsKey = ["settings", "integrations"] as const;

export const useTools = () =>
  useQuery({
    queryKey: toolsKey,
    queryFn: () => api<ToolsResponse>("GET", "/api/tools"),
    staleTime: 0,
  });

export const useUsage = () =>
  useQuery({
    queryKey: usageKey,
    queryFn: () => api<Usage>("GET", "/api/usage"),
    staleTime: 0,
  });
